import { A } from "@solidjs/router";
import {
  TbAddressBook,
  TbBolt,
  TbBrandAndroid,
  TbCheck,
  TbDevices,
  TbExternalLink,
  TbInfinity,
  TbKey,
  TbMail,
  TbMicrophone,
  TbPlugConnected,
  TbQuote,
  TbRobotOff,
  TbSend,
  TbTicket,
  TbUsers,
} from "solid-icons/tb";
import { createSignal, For, type JSX, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { EXPLORE_PLAN, formatUsd, PAID_PLANS } from "~/components/billing/planFeatures";
import {
  type Cell,
  COMPETITORS,
  SRC,
  SRC_NAME,
  STATUS,
  TABLE_GROUPS,
  type Vendor,
  type VendorKey,
  VENDORS,
} from "~/components/landing/compareData";
import { CompareTimeline, CompetitorMarks } from "~/components/landing/CompareTimeline";
import { ClosingCTA } from "~/components/marketing/ClosingCTA";
import { AGENTS, BUILT_IN, type Logo, MCP_SERVERS, MODEL_PROVIDERS } from "~/components/marketing/content";
import { DownloadButton, GetStartedButton } from "~/components/marketing/CTAButtons";
import { agentHandoffDemo, listenFlowDemo, multiplayerDemo, offerFlowDemo } from "~/components/marketing/demo";
import { LogoMark, LogoStack } from "~/components/marketing/LogoMark";
import {
  AgentHandoffMockup,
  Caret,
  ChatExchange,
  HandoffCard,
  ListenFlowMockup,
  MultiplayerMockup,
  OfferFlowMockup,
  SelectedNotes,
} from "~/components/marketing/mockups";
import { TrustSection } from "~/components/marketing/TrustSections";
import { BRAND_TEXT, Eyebrow, PageHero, SectionTitle, SplitRow, TextLink } from "~/components/marketing/ui";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import { hasChiefBuild } from "~/lib/chiefDownload";
import { useOS } from "~/lib/useOS";

type Reason = { lead: TranslationKey; text: TranslationKey; icon: typeof TbBolt; color: string; logos?: Logo[] };

const pick = (logos: Logo[], names: string[]) => logos.filter((logo) => names.includes(logo.name));

const CHOOSE_CHIEF: Reason[] = [
  {
    lead: "marketing.compare.short.act_lead",
    text: "marketing.compare.short.act_text",
    icon: TbBolt,
    color: "#3049cf",
  },
  {
    lead: "marketing.compare.short.tools_lead",
    text: "marketing.compare.short.tools_text",
    icon: TbPlugConnected,
    color: "#5E6AD2",
    logos: pick(BUILT_IN, ["Linear", "GitHub", "Slack"]),
  },
  {
    lead: "marketing.compare.short.setup_lead",
    text: "marketing.compare.short.setup_text",
    icon: TbKey,
    color: "#d97706",
    logos: [...MODEL_PROVIDERS, ...pick(AGENTS, ["Claude Code", "Cursor"])],
  },
  {
    lead: "marketing.compare.short.crm_lead",
    text: "marketing.compare.short.crm_text",
    icon: TbAddressBook,
    color: "#ea580c",
    logos: pick(MCP_SERVERS, ["HubSpot", "Salesforce"]),
  },
];

const CHOOSE_OTHER: (Reason & { who: { name: string; detail: TranslationKey; href: string } })[] = [
  {
    lead: "marketing.compare.short.android_lead",
    text: "marketing.compare.short.android_text",
    icon: TbBrandAndroid,
    color: "#16a34a",
    who: { name: "Otter", detail: "marketing.compare.short.android_who", href: SRC.otterApps },
  },
  {
    lead: "marketing.compare.short.free_lead",
    text: "marketing.compare.short.free_text",
    icon: TbInfinity,
    color: "#0891b2",
    who: { name: "Fathom", detail: "marketing.compare.short.free_who", href: SRC.fathomPricing },
  },
];

const HERO_PROOF: { icon: typeof TbBolt; color: string; label: TranslationKey }[] = [
  { icon: TbRobotOff, color: "#1d9e75", label: "marketing.compare.hero.no_bot" },
  { icon: TbDevices, color: "#64748b", label: "marketing.compare.hero.platforms" },
  { icon: TbUsers, color: "#e5484d", label: "marketing.compare.hero.coedit" },
];

const HERO_TOOLS = pick(BUILT_IN, ["Linear", "GitHub", "Slack", "Gmail"]);

const INSTALL_FAQ = {
  q: "What do I need to install?",
  a: "The Chief desktop app for macOS or Windows. It captures the call from your computer, so no bot joins. For in-person conversations, the iPhone app records with your phone's microphone.",
};

const APPROVAL_FAQ = {
  q: "Does Chief change connected tools automatically?",
  a: "Only with your approval. Chief asks before it changes Linear, GitHub, Slack, or Gmail, unless you've pre-approved that kind of change.",
};

const FAQ = [
  {
    q: "Can I try Chief alongside my current tool?",
    a: "Yes. Run your next call in Chief next to the tool you use today, and compare what each one leaves you with.",
  },
  INSTALL_FAQ,
  {
    q: "Who can view or edit a shared session?",
    a: "People with editing access can collaborate on the session's documents and canvas. Anyone with the guest link can follow the transcript, summary, and brief in a browser, without an account.",
  },
  APPROVAL_FAQ,
  {
    q: "Does it connect to my CRM?",
    a: "HubSpot, Salesforce, Attio and Close, through MCP. On the Pro plan, an admin adds their MCP servers from the catalog, and Chief asks before it writes anything.",
  },
  {
    q: "What does the free allowance cover?",
    a: `You get ${EXPLORE_PLAN.signupCredits.toLocaleString("en-US")} credits when you sign up, and free credits refill at ${EXPLORE_PLAN.dailyCredits} a day. Credits pay for Chief's AI work, such as summaries and chats.`,
  },
];

export function ComparePage() {
  const { t } = useI18n();
  return (
    <>
      <HeroSection />
      <ShortAnswerSection />
      <CompareTimeline />
      <TableSection vendors={VENDORS} />
      <CompareLinks />
      <CapabilitiesSection />
      <FaqSection
        id="compare-faq"
        eyebrow={t("marketing.compare.faq.eyebrow")}
        title={t("marketing.compare.faq.title")}
        items={FAQ}
      />
      <ClosingCTA id="compare-closing" title="Try Chief on your next call" from="compare page" />
    </>
  );
}

export function AlternativePage(props: { vendor: Vendor }) {
  const faq = () => [...props.vendor.faq, INSTALL_FAQ, APPROVAL_FAQ];
  return (
    <>
      <PageHero
        eyebrow={`${props.vendor.name} alternative`}
        title={props.vendor.h1}
        sub={props.vendor.answer}
        from="alternatives hero"
      />
      <TableSection vendors={[props.vendor]} />
      <WhyLookSection vendor={props.vendor} />
      <ComparisonSection vendor={props.vendor} />
      <PricingSection vendor={props.vendor} />
      <FitSection vendor={props.vendor} />
      <MovingOverSection vendor={props.vendor} />
      <FaqSection id="alternative-faq" eyebrow="FAQ" title={"Questions\nbefore you switch."} items={faq()} />
      <script
        type="application/ld+json"
        innerHTML={JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq().map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        })}
      />
      <TrustSection />
      <CompareLinks vendor={props.vendor} />
      <ClosingCTA id="alternative-closing" title="Try Chief on your next call" from="alternatives closing" />
    </>
  );
}

const PROOF_PILL =
  "flex items-center gap-2.5 rounded-full border border-primary-border bg-tile/80 py-1.5 ps-1.5 pe-4 text-start shadow-sm backdrop-blur sm:whitespace-nowrap";

// Not PageHero: its visual slot sits a full mockup's distance below the buttons, too far for a proof row.
const HeroSection = () => {
  const { t } = useI18n();
  const os = useOS();
  const title = () => t("marketing.compare.hero.title").split("\n");
  return (
    <section class="relative overflow-hidden px-4 pt-14 pb-16 sm:px-6 sm:py-20 lg:px-8">
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--color-primary-border)_1px,transparent_1px)] mask-[radial-gradient(ellipse_at_top,black_30%,transparent_75%)] bg-size-[24px_24px]"
      />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-40 left-1/2 h-112 w-240 -translate-x-1/2 rounded-full bg-brand/10 blur-3xl"
      />
      <div class="relative mx-auto flex max-w-5xl flex-col items-center text-center">
        <div class="flex items-center gap-3">
          <span aria-hidden="true">
            <CompetitorMarks size="sm" />
          </span>
          <Eyebrow label={t("marketing.compare.hero.eyebrow")} />
        </div>
        <h1 class="mt-5 text-4xl font-semibold tracking-tight text-balance sm:text-6xl">
          {title()[0]}
          <Show when={title()[1]}>
            {(second) => <span class="block text-brand dark:text-brand-hover">{second()}</span>}
          </Show>
        </h1>
        <p class="mt-6 max-w-2xl text-lg leading-relaxed text-balance text-secondary-text sm:text-xl">
          {t("marketing.compare.hero.sub")}
        </p>
        <div class="mt-9 flex flex-wrap items-center justify-center gap-3">
          <DownloadButton from="compare hero" size="lg" />
          <Show when={hasChiefBuild(os())}>
            <GetStartedButton variant="secondary" size="lg" />
          </Show>
        </div>
        <ul class="mt-10 flex flex-wrap justify-center gap-3 text-sm text-secondary-text">
          <For each={HERO_PROOF}>
            {(proof) => (
              <li class={PROOF_PILL} style={{ "--c": proof.color }}>
                <span class="mk-ink grid size-7 shrink-0 place-items-center rounded-full bg-(--c)/12 ring-1 ring-(--c)/20 dark:bg-(--c)/20">
                  <Dynamic component={proof.icon} class="size-4" aria-hidden="true" />
                </span>
                {t(proof.label)}
              </li>
            )}
          </For>
          <li class={PROOF_PILL}>
            <LogoStack logos={HERO_TOOLS} />
            <span>
              {t("marketing.compare.hero.tools")}
              <span class="sr-only">: {HERO_TOOLS.map((logo) => logo.name).join(", ")}</span>
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
};

const SourceLink = (props: { href: string; label: string }) => (
  <a
    href={props.href}
    target="_blank"
    rel="noopener noreferrer"
    class="inline-flex items-center text-secondary-text hover:text-primary-text"
    aria-label={`Source: ${props.label}`}
  >
    <TbExternalLink class="size-3" aria-hidden="true" />
  </a>
);

const VendorMark = (props: { name: string; class: string }) => (
  <Show when={COMPETITORS.find((c) => c.name === props.name)}>
    {(c) => (
      <span class={`grid shrink-0 place-items-center overflow-hidden rounded-full ${c().bg} ${props.class}`}>
        <img
          src={c().src}
          alt=""
          class={c().bg ? "size-3/5 object-contain brightness-0 invert" : "size-full object-cover"}
        />
      </span>
    )}
  </Show>
);

const ReasonItem = (props: { reason: Reason; children?: JSX.Element }) => {
  const { t } = useI18n();
  return (
    <li class="flex gap-4" style={{ "--c": props.reason.color }}>
      <span class="mk-ink grid size-10 shrink-0 place-items-center rounded-xl bg-(--c)/12 ring-1 ring-(--c)/20 dark:bg-(--c)/20">
        <Dynamic component={props.reason.icon} class="size-5" aria-hidden="true" />
      </span>
      <div class="flex min-w-0 flex-col gap-1">
        <p class="leading-snug font-semibold">{t(props.reason.lead)}</p>
        <p class="text-[15px] leading-relaxed text-secondary-text">{t(props.reason.text)}</p>
        <Show when={props.reason.logos}>
          {(logos) => (
            <span class="mt-1.5 flex flex-wrap items-center gap-1.5">
              <For each={logos()}>
                {(logo) => (
                  <span
                    title={logo.name}
                    class="grid size-7 place-items-center rounded-lg border border-primary-border bg-tile shadow-sm"
                  >
                    <LogoMark mark={logo.mark} class="size-4" />
                    <span class="sr-only">{logo.name}</span>
                  </span>
                )}
              </For>
            </span>
          )}
        </Show>
        {props.children}
      </div>
    </li>
  );
};

// Chief's four reasons sit two by two in the wider card, so it ends level with the narrower card's two.
const ShortAnswerSection = () => {
  const { t } = useI18n();
  return (
    <section aria-labelledby="short-answer" class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <Eyebrow label={t("marketing.compare.short.eyebrow")} />
        <SectionTitle id="short-answer" text={t("marketing.compare.short.title")} class="mt-5" />
        <div class="mt-12 grid gap-6 lg:grid-cols-5">
          <div class={`${CHIEF_CARD} lg:col-span-3`}>
            <ChiefCardTitle label={t("marketing.compare.short.chief_label")} />
            <ul class="grid gap-x-8 gap-y-7 sm:grid-cols-2">
              <For each={CHOOSE_CHIEF}>{(item) => <ReasonItem reason={item} />}</For>
            </ul>
          </div>
          <div class={`${VENDOR_CARD} lg:col-span-2`}>
            <h3 class="mb-7 flex items-center gap-3 text-lg font-semibold">
              <CompetitorMarks />
              <span class="text-secondary-text">{t("marketing.compare.short.other_label")}</span>
            </h3>
            <ul class="flex flex-col gap-7">
              <For each={CHOOSE_OTHER}>
                {(item) => (
                  <ReasonItem reason={item}>
                    <span class="mt-2 inline-flex items-center gap-2 text-[13px] text-secondary-text">
                      <VendorMark name={item.who.name} class="size-5" />
                      <span>
                        <span class="font-semibold text-primary-text">{item.who.name}:</span> {t(item.who.detail)}
                      </span>
                      <SourceLink href={item.who.href} label={`${item.who.name}, ${t(item.who.detail)}`} />
                    </span>
                  </ReasonItem>
                )}
              </For>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const CellContent = (props: { cell: Cell; vendor: string; row: string }) => {
  const source = (href: string) => <SourceLink href={href} label={`${props.vendor}, ${props.row}`} />;
  return (
    <>
      <span class="relative inline-flex items-center">
        <Dynamic
          component={STATUS[props.cell.status].icon}
          class={`size-5 shrink-0 ${STATUS[props.cell.status].class}`}
          aria-hidden="true"
        />
        <span class="sr-only">{STATUS[props.cell.status].label}</span>
        {/* With no caption to follow, the link hangs off the icon's right edge so the icon stays on the column's centre. */}
        <Show when={!props.cell.note && props.cell.href}>
          {(href) => <span class="absolute left-full ml-1.5 flex">{source(href())}</span>}
        </Show>
      </span>
      <Show when={props.cell.note}>
        {(note) => (
          <span class="mt-1 block text-[13px] text-secondary-text">
            {/* The last word rides with the link so a wrapping caption never strands the link on its own line. */}
            <Show when={props.cell.href} fallback={note()}>
              {(href) => (
                <>
                  {note().slice(0, note().lastIndexOf(" ") + 1)}
                  <span class="whitespace-nowrap">
                    {note().slice(note().lastIndexOf(" ") + 1)}
                    <span class="ml-1 inline-flex align-[-1px]">{source(href())}</span>
                  </span>
                </>
              )}
            </Show>
          </span>
        )}
      </Show>
    </>
  );
};

const TableSection = (props: { vendors: Vendor[] }) => {
  const { t } = useI18n();
  const [mobileVendor, setMobileVendor] = createSignal<VendorKey>(props.vendors[0].key);
  const mobileVendorName = () => props.vendors.find((v) => v.key === mobileVendor())?.name ?? "";

  return (
    <section aria-labelledby="compare-table" class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <Eyebrow label={t("marketing.compare.table.eyebrow")} />
        <SectionTitle id="compare-table" text={t("marketing.compare.table.title")} class="mt-5 mb-12" />

        <div class="hidden rounded-2xl border border-primary-border bg-tile md:block">
          <table class="w-full table-fixed border-separate border-spacing-0 text-[15px]">
            <thead>
              <tr class="text-left">
                <th
                  scope="col"
                  class="sticky top-16 z-10 w-52 rounded-tl-2xl border-b border-primary-border bg-panel px-5 py-4"
                >
                  <span class="sr-only">Capability</span>
                </th>
                <th
                  scope="col"
                  class="sticky top-16 z-10 border-b border-primary-border bg-panel px-5 py-4 text-center font-bold"
                >
                  <span class="flex items-center justify-center gap-2">
                    <span class="grid size-6 place-items-center rounded-full border border-accent/40 bg-tile">
                      <img src="/images/chief-logo-gold-crown-only.svg" alt="" class="h-2.5 w-auto" />
                    </span>
                    Chief
                  </span>
                </th>
                <For each={props.vendors}>
                  {(vendor, i) => (
                    <th
                      scope="col"
                      class={`sticky top-16 z-10 border-b border-primary-border bg-panel px-5 py-4 text-center font-semibold ${
                        i() === props.vendors.length - 1 ? "rounded-tr-2xl" : ""
                      }`}
                    >
                      <span class="flex items-center justify-center gap-2">
                        <VendorMark name={vendor.name} class="size-6" />
                        {vendor.name}
                      </span>
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <For each={TABLE_GROUPS}>
              {(group) => (
                <tbody>
                  <tr>
                    <th
                      scope="rowgroup"
                      colSpan={props.vendors.length + 2}
                      class="border-b border-primary-border bg-canvas px-5 py-2.5 text-left text-[13px] font-semibold tracking-wider text-secondary-text uppercase"
                    >
                      {group.name}
                    </th>
                  </tr>
                  <For each={group.rows}>
                    {(row) => (
                      <tr class="group align-top">
                        <th
                          scope="row"
                          class="border-b border-primary-border px-5 py-4 text-left font-semibold group-hover:bg-secondary-text/5"
                        >
                          {row.label}
                        </th>
                        <td class="border-b border-primary-border bg-brand/5 px-5 py-4 text-center group-hover:bg-brand/10">
                          <CellContent cell={row.chief} vendor="Chief" row={row.label} />
                        </td>
                        <For each={props.vendors}>
                          {(vendor) => (
                            <td class="border-b border-primary-border px-5 py-4 text-center group-hover:bg-secondary-text/5">
                              <CellContent cell={row[vendor.key]} vendor={vendor.name} row={row.label} />
                            </td>
                          )}
                        </For>
                      </tr>
                    )}
                  </For>
                </tbody>
              )}
            </For>
          </table>
        </div>

        <div class="md:hidden">
          <Show when={props.vendors.length > 1}>
            <label class="mb-4 flex items-center gap-3 text-sm">
              <span class="text-secondary-text">Compare Chief with</span>
              <select
                value={mobileVendor()}
                onChange={(e) => setMobileVendor(e.currentTarget.value as VendorKey)}
                class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-primary-text"
              >
                <For each={props.vendors}>{(vendor) => <option value={vendor.key}>{vendor.name}</option>}</For>
              </select>
            </label>
          </Show>
          <For each={TABLE_GROUPS}>
            {(group) => (
              <div class="mb-6">
                <h3 class="mb-2 text-[13px] font-semibold tracking-wider text-secondary-text uppercase">
                  {group.name}
                </h3>
                <div class="divide-y divide-primary-border rounded-xl border border-primary-border">
                  <For each={group.rows}>
                    {(row) => (
                      <div class="p-4">
                        <p class="mb-2 font-semibold">{row.label}</p>
                        <div class="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p class="mb-0.5 text-xs font-semibold text-brand">Chief</p>
                            <CellContent cell={row.chief} vendor="Chief" row={row.label} />
                          </div>
                          <div>
                            <p class="mb-0.5 flex items-center gap-1.5 text-xs font-semibold">
                              <VendorMark name={mobileVendorName()} class="size-4" />
                              {mobileVendorName()}
                            </p>
                            <CellContent cell={row[mobileVendor()]} vendor={mobileVendorName()} row={row.label} />
                          </div>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
        <p class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-secondary-text">
          <For each={Object.values(STATUS)}>
            {(s) => (
              <span class="inline-flex items-center gap-1.5">
                <Dynamic component={s.icon} class={`size-4 shrink-0 ${s.class}`} aria-hidden="true" />
                {s.label}
              </span>
            )}
          </For>
        </p>
        <p class="mt-2 text-[13px] text-secondary-text">
          Vendor facts checked{" "}
          <For each={props.vendors}>
            {(vendor, i) => (
              <>
                {i() > 0 && " · "}
                {vendor.name}: {vendor.checked}
              </>
            )}
          </For>
        </p>
        <p class="mt-2 text-[13px] text-secondary-text">
          We build Chief, so read this as a vendor's comparison. Every competitor fact links to that vendor's own page.
        </p>
      </div>
    </section>
  );
};

const CompareLinks = (props: { vendor?: Vendor }) => (
  <nav aria-label="More comparisons" class="px-4 sm:px-6 lg:px-8">
    <p class="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 border-t border-primary-border py-8 text-[15px]">
      <span class="text-secondary-text">{props.vendor ? "More comparisons:" : "One tool at a time:"}</span>
      <Show when={props.vendor}>
        <TextLink href="/compare" label="Chief vs Granola, Otter, and Fathom" />
      </Show>
      <For each={VENDORS.filter((vendor) => vendor.key !== props.vendor?.key)}>
        {(vendor) => <TextLink href={`/alternatives/${vendor.key}`} label={`${vendor.name} alternative`} />}
      </For>
    </p>
  </nav>
);

const CHIEF_CARD =
  "relative overflow-hidden rounded-2xl border border-brand/40 bg-tile bg-linear-to-br from-brand/10 to-transparent to-50% p-6 shadow-lg shadow-brand/10 sm:p-8 dark:border-brand-hover/40";
const VENDOR_CARD = "rounded-2xl border border-primary-border bg-tile p-6 sm:p-8";

const ChiefCardTitle = (props: { label: string; href?: string }) => (
  <h3 class="mb-7 flex items-center gap-3 text-lg font-semibold">
    <span class="grid size-10 place-items-center rounded-xl border border-accent/40 bg-tile shadow-sm">
      <img src="/images/chief-logo-gold-crown-only.svg" alt="" class="h-4 w-auto" />
    </span>
    <Show when={props.href} fallback={<span class={BRAND_TEXT}>{props.label}</span>}>
      {(href) => (
        <A href={href()} class={`hover:underline ${BRAND_TEXT}`}>
          {props.label}
        </A>
      )}
    </Show>
  </h3>
);

const VendorCardTitle = (props: { vendor: Vendor; label: string; href?: string }) => (
  <h3 class="mb-7 flex items-center gap-3 text-lg font-semibold">
    <VendorMark name={props.vendor.name} class="size-10" />
    <span class="text-secondary-text">{props.label}</span>
    <Show when={props.href}>{(href) => <SourceLink href={href()} label={props.label} />}</Show>
  </h3>
);

const SourceLinks = (props: { hrefs: string[] }) => (
  <For each={props.hrefs}>
    {(href, i) => (
      <>
        {i() > 0 && ", "}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-0.5 underline decoration-primary-border underline-offset-2 hover:text-primary-text"
        >
          {SRC_NAME[href]}
          <TbExternalLink class="size-3" aria-hidden="true" />
        </a>
      </>
    )}
  </For>
);

/** Title on the left, content on the right: the layout of the homepage FAQ. */
const SideTitled = (props: { id: string; eyebrow: string; title: string; children: JSX.Element }) => (
  <section aria-labelledby={props.id} class="border-t border-primary-border px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
    <div class="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <div class="flex flex-col gap-5">
        <Eyebrow label={props.eyebrow} />
        <SectionTitle id={props.id} text={props.title} />
      </div>
      <div>{props.children}</div>
    </div>
  </section>
);

const TwoCards = (props: { id: string; eyebrow: string; title: string; children: JSX.Element }) => (
  <section aria-labelledby={props.id} class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <Eyebrow label={props.eyebrow} />
      <SectionTitle id={props.id} text={props.title} class="mt-5" />
      <div class="mt-12 grid gap-6 md:grid-cols-2">{props.children}</div>
    </div>
  </section>
);

const WhyLookSection = (props: { vendor: Vendor }) => (
  <SideTitled
    id="why-look"
    eyebrow="Why people switch"
    title={`Why people look for\n${/^[AEIOU]/.test(props.vendor.name) ? "an" : "a"} ${props.vendor.name} alternative`}
  >
    <dl class="divide-y divide-primary-border border-y border-primary-border">
      <For each={props.vendor.whyLook}>
        {(item) => (
          <div class="py-6">
            <dt class="text-lg font-semibold">{item.lead}</dt>
            <dd class="mt-2 leading-relaxed text-secondary-text">
              {item.text} <SourceLink href={item.href} label={`${props.vendor.name}, ${item.lead}`} />
            </dd>
          </div>
        )}
      </For>
    </dl>
    <Show when={props.vendor.quote}>
      {(quote) => (
        <figure class="mt-8 flex flex-col gap-5 rounded-2xl border border-primary-border bg-tile p-6">
          <TbQuote class="size-8 text-accent" aria-hidden="true" />
          <blockquote class="text-lg leading-snug font-medium">{quote().text}</blockquote>
          <figcaption class="border-t border-primary-border pt-4 font-mono text-xs text-secondary-text">
            <a href={quote().href} target="_blank" rel="noopener noreferrer" class="hover:text-primary-text">
              {quote().who}, {quote().where}, {quote().date}
            </a>
          </figcaption>
        </figure>
      )}
    </Show>
  </SideTitled>
);

const AREA_IDS = ["area-during", "area-after", "area-setup"];

const ComparisonSection = (props: { vendor: Vendor }) => {
  const { t } = useI18n();
  const area = (i: number) => props.vendor.compare[i];
  const links = (i: number) => (
    <p class="text-sm text-secondary-text">
      {props.vendor.name} sources: <SourceLinks hrefs={area(i).hrefs} />
    </p>
  );
  const visuals = [
    <MultiplayerMockup data={multiplayerDemo(t)} />,
    <OfferFlowMockup data={offerFlowDemo(t)} />,
    <AgentHandoffMockup data={agentHandoffDemo(t)} />,
  ];
  return (
    <>
      <section aria-labelledby="area-capture" class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
        <div class="mx-auto max-w-7xl">
          <Eyebrow index="01" label={area(0).area} />
          <SectionTitle id="area-capture" text={area(0).title} class="mt-5" />
          <p class="mt-5 max-w-3xl text-lg leading-relaxed text-secondary-text">
            {area(0).text} ({props.vendor.name} sources: <SourceLinks hrefs={area(0).hrefs} />)
          </p>
          <div class="mt-12">
            <ListenFlowMockup data={listenFlowDemo(t)} />
          </div>
        </div>
      </section>
      <For each={AREA_IDS}>
        {(id, i) => (
          <SplitRow
            id={id}
            index={`0${i() + 2}`}
            eyebrow={area(i() + 1).area}
            title={area(i() + 1).title}
            body={area(i() + 1).text}
            visual={visuals[i()]}
            reverse={i() % 2 === 0}
          >
            {links(i() + 1)}
          </SplitRow>
        )}
      </For>
    </>
  );
};

const CHIEF_PLANS = [
  {
    name: "Explore",
    price: "$0",
    note: `${EXPLORE_PLAN.signupCredits.toLocaleString("en-US")} credits when you sign up, refilling at ${EXPLORE_PLAN.dailyCredits} a day`,
  },
  {
    name: "Pro",
    price: `${formatUsd(PAID_PLANS.pro.monthly.usd)} a month`,
    note: `or ${formatUsd(PAID_PLANS.pro.yearly.usd)} a year`,
  },
  {
    name: "Power User",
    price: `${formatUsd(PAID_PLANS.powerUser.monthly.usd)} a month`,
    note: `or ${formatUsd(PAID_PLANS.powerUser.yearly.usd)} a year`,
  },
  { name: "Enterprise", price: "Custom" },
];

const PlanList = (props: { plans: Vendor["pricing"]["tiers"] }) => (
  <ul class="flex flex-col divide-y divide-primary-border">
    <For each={props.plans}>
      {(plan) => (
        <li class="flex items-baseline justify-between gap-4 py-4 first:pt-0 last:pb-0">
          <span class="shrink-0 font-semibold">{plan.name}</span>
          <span class="min-w-0 text-right">
            <span class="text-lg font-semibold tracking-tight">{plan.price}</span>
            <Show when={plan.note}>{(note) => <span class="block text-sm text-secondary-text">{note()}</span>}</Show>
          </span>
        </li>
      )}
    </For>
  </ul>
);

const PricingSection = (props: { vendor: Vendor }) => (
  <TwoCards id="pricing" eyebrow="Pricing" title={"What each costs\nfor you and for a team"}>
    <div class={CHIEF_CARD}>
      <ChiefCardTitle label="Chief" href="/pricing" />
      <PlanList plans={CHIEF_PLANS} />
    </div>
    <div class={VENDOR_CARD}>
      <VendorCardTitle vendor={props.vendor} label={props.vendor.name} href={props.vendor.pricing.href} />
      <PlanList plans={props.vendor.pricing.tiers} />
    </div>
    <p class="max-w-3xl leading-relaxed text-secondary-text md:col-span-2">
      {props.vendor.pricing.team} On Chief, one Pro subscription covers the whole organization:{" "}
      {formatUsd(PAID_PLANS.pro.monthly.usd)} a month, and members share its credits. Credits pay for Chief's AI work.
    </p>
  </TwoCards>
);

const FitSection = (props: { vendor: Vendor }) => (
  <TwoCards id="fit" eyebrow="Fit" title={"Switch, or stay?\nBoth tools do a job well."}>
    <div class={CHIEF_CARD}>
      <ChiefCardTitle label="Switch to Chief if…" />
      <ul class="flex flex-col gap-5">
        <For each={props.vendor.switchIf}>
          {(item) => (
            <li class="flex gap-3 leading-relaxed">
              <TbCheck class="mt-1 size-5 shrink-0 text-brand dark:text-brand-hover" aria-hidden="true" />
              {item}
            </li>
          )}
        </For>
      </ul>
    </div>
    <div class={VENDOR_CARD}>
      <VendorCardTitle vendor={props.vendor} label={`Stay with ${props.vendor.name} if…`} />
      <ul class="flex flex-col gap-5">
        <For each={props.vendor.stayIf}>
          {(item) => (
            <li class="leading-relaxed">
              <p class="font-semibold">{item.lead}</p>
              <p class="text-[15px] text-secondary-text">
                {item.text} <SourceLink href={item.href} label={`${props.vendor.name}, ${item.lead}`} />
              </p>
            </li>
          )}
        </For>
      </ul>
    </div>
  </TwoCards>
);

const MovingOverSection = (props: { vendor: Vendor }) => (
  <SideTitled id="moving-over" eyebrow="Moving over" title={`Moving over\nfrom ${props.vendor.name}`}>
    <p class="text-lg leading-relaxed text-secondary-text">
      {props.vendor.movingOver.text}{" "}
      <SourceLink href={props.vendor.movingOver.href} label={`${props.vendor.name}, exporting a transcript`} />
    </p>
  </SideTitled>
);

const FaqSection = (props: {
  id: string;
  eyebrow: string;
  title: string;
  items: { q: string; a: string; href?: string }[];
}) => (
  <SideTitled id={props.id} eyebrow={props.eyebrow} title={props.title}>
    <dl class="divide-y divide-primary-border border-y border-primary-border">
      <For each={props.items}>
        {(item) => (
          <div class="py-6">
            <dt class="text-lg font-semibold">{item.q}</dt>
            <dd class="mt-2 leading-relaxed text-secondary-text">
              {item.a}
              <Show when={item.href}>
                {(href) => (
                  <span class="ml-1.5 inline-flex align-[-1px]">
                    <SourceLink href={href()} label={item.q} />
                  </span>
                )}
              </Show>
            </dd>
          </div>
        )}
      </For>
    </dl>
  </SideTitled>
);

// Product crops for the capability panels, drawn with the desktop app's own pieces
// from the mockups: a shared document's carets, a chat answer with its sources, the
// Turn into… menu on selected notes, and the Summary's Send to AI agent card.
const CROP = "mk-app rounded-xl border border-primary-border bg-canvas p-4 text-primary-text";

const SharedDocCrop = () => (
  <div class={`${CROP} p-5 text-[15px] leading-relaxed`}>
    <p class="mb-3 text-lg font-semibold">Launch plan</p>
    <p class="mb-3 text-secondary-text">
      Launch moves to the 14th. The pricing page ships the same morning
      <Caret person={{ name: "Sam", color: "#a78bfa" }} />, with the blog post and the customer email.
    </p>
    <p class="text-secondary-text">
      <span class="rounded-sm bg-note-highlight/25">Open question: annual pricing for Dana.</span>
      <Caret person={{ name: "Priya", color: "#2dd4bf" }} />
    </p>
  </div>
);

const Cite = (props: { n: number }) => <span class="text-blue-600 dark:text-blue-400">[{props.n}]</span>;

const ContextAnswerCrop = () => (
  <div class={CROP}>
    <ChatExchange
      question="What did we promise Acme about SSO?"
      answer={
        <>
          SSO by Q3, promised on the March 4 call <Cite n={1} />. The Linear ticket is in progress <Cite n={2} />, and
          Dana asked for a date on Tuesday <Cite n={3} />.
        </>
      }
      sources={[
        { icon: <TbMicrophone class="size-3.5 text-session-icon" aria-hidden="true" />, label: "Acme sync · Mar 4" },
        { icon: <TbTicket class="size-3.5" aria-hidden="true" />, label: "ENG-3310" },
        { icon: <TbMail class="size-3.5" aria-hidden="true" />, label: "Re: SSO timeline" },
      ]}
    />
  </div>
);

const TurnIntoCrop = () => (
  <div class={`${CROP} pt-16 pb-8`}>
    <SelectedNotes
      lines={[
        "Launch moves to the 14th.",
        "Sam drafts the announcement by Thursday.",
        "The pricing page ships with the launch.",
      ]}
      formats={["PRD", "Brief", "Plan", "Canvas"]}
      picked={0}
    />
  </div>
);

const HandoffCrop = () => (
  <div class={CROP}>
    <HandoffCard agent="Claude Code" folder="~/code/launch-site">
      <span class="flex min-h-10 items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-medium text-white">
        <TbSend class="size-4" aria-hidden="true" />
        Send to AI agent
      </span>
    </HandoffCard>
  </div>
);

const CAPABILITY_PANELS: { name: string; sentence: string; crop: () => JSX.Element; more: string[] }[] = [
  {
    name: "Work together",
    sentence: "People with editing access write the plan together while the call runs.",
    crop: () => <SharedDocCrop />,
    more: ["Work on the shared canvas as you talk.", "Guests follow the transcript and summary in a browser."],
  },
  {
    name: "Use your project's context",
    sentence: "Ask across connected tools and previous sessions, with source references where available.",
    crop: () => <ContextAnswerCrop />,
    more: [
      "Auto-apply Tags label the live summary using instructions you write.",
      "Meeting-prep Actions bring earlier decisions into a session's brief.",
    ],
  },
  {
    name: "Develop the work",
    sentence: "Select the notes that matter and turn them into a working draft.",
    crop: () => <TurnIntoCrop />,
    more: [
      "Ask an Expert Voice, such as Coach, for another perspective.",
      "On the Pro plan, update Linear, GitHub, Slack, and Gmail, with approval controls.",
    ],
  },
  {
    name: "Connect your own setup",
    sentence: "Hand a session to the coding agent on your machine, with the context it needs.",
    crop: () => <HandoffCrop />,
    more: [
      "Use your own OpenAI, Anthropic, or OpenRouter keys.",
      "MCP clients read and write sessions, memories, and projects.",
    ],
  },
];

const CapabilitiesSection = () => {
  const { t } = useI18n();
  return (
    <section aria-labelledby="compare-capabilities" class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <Eyebrow label={t("marketing.compare.capabilities.eyebrow")} />
        <SectionTitle id="compare-capabilities" text={t("marketing.compare.capabilities.title")} class="mt-5" />
        <div class="mt-12 grid gap-6 md:grid-cols-2">
          <For each={CAPABILITY_PANELS}>
            {(panel) => (
              <div class="flex flex-col rounded-2xl border border-primary-border bg-tile p-6 sm:p-8">
                <h3 class="mb-2 text-2xl font-semibold tracking-tight">{panel.name}</h3>
                <p class="mb-6 leading-relaxed text-secondary-text">{panel.sentence}</p>
                {panel.crop()}
                <ul class="mt-6 flex flex-col gap-2.5 text-[15px] text-secondary-text">
                  <For each={panel.more}>
                    {(line) => (
                      <li class="flex items-start gap-2.5">
                        <span class="mt-2 size-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                        {line}
                      </li>
                    )}
                  </For>
                </ul>
              </div>
            )}
          </For>
        </div>
      </div>
    </section>
  );
};
